import { useState } from 'react'
import { AlertTriangle, History } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MonthFilter, type MonthValue } from './MonthFilter'
import { ExportarMenu, type ExportarMenuTipoOption } from './ExportarMenu'
import type { PeriodoModo, RangoParcial } from '../hooks/usePeriodoReporte'

interface ReportePeriodoBarProps {
  rango: { desde: string; hasta: string }
  mes: MonthValue
  onMesChange: (value: MonthValue) => void
  minDate?: string | null
  maxDate?: string | null
  configId: number
  colorNombre: string
  /** Tipos de export disponibles en esta página. */
  exportTipos: ExportarMenuTipoOption[]
  /** Filtros activos que se reenvían al endpoint de export. */
  exportFiltros?: Record<string, string | number | boolean | undefined>
  /**
   * Si está definido, muestra el botón "Todo el histórico" que desactiva el
   * filtro de fecha para ver las estadísticas completas.
   */
  sinFecha?: boolean
  onSinFechaChange?: (value: boolean) => void
  /** Modo del selector de fecha: por mes completo o rango personalizado. */
  modo?: PeriodoModo
  onModoChange?: (modo: PeriodoModo) => void
  rangoPersonalizado?: RangoParcial
  onRangoPersonalizadoChange?: (rango: RangoParcial) => void
}

const RATE_LIMIT_WARN_THRESHOLD = 20

export function ReportePeriodoBar({
  rango,
  mes,
  onMesChange,
  minDate,
  maxDate,
  configId,
  colorNombre,
  exportTipos,
  exportFiltros,
  sinFecha = false,
  onSinFechaChange,
  modo = 'mes',
  onModoChange,
  rangoPersonalizado = {},
  onRangoPersonalizadoChange,
}: ReportePeriodoBarProps) {
  const [rateRemaining, setRateRemaining] = useState<number | null>(null)

  // El toggle Mes/Rango solo se muestra si el padre cablea el modo.
  const soportaRango = Boolean(onModoChange && onRangoPersonalizadoChange)
  const enRango = soportaRango && modo === 'rango'

  const setRango = (campo: 'desde' | 'hasta', value: string) => {
    const next = { ...rangoPersonalizado }
    if (!value) delete next[campo]
    else next[campo] = value
    onRangoPersonalizadoChange?.(next)
  }

  return (
    <>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground">
          Periodo:{' '}
          {sinFecha ? (
            <strong>Todo el histórico</strong>
          ) : enRango ? (
            <>
              <strong>{rangoPersonalizado.desde || '—'}</strong> →{' '}
              <strong>{rangoPersonalizado.hasta || '—'}</strong>
            </>
          ) : (
            <>
              <strong>{rango.desde}</strong> → <strong>{rango.hasta}</strong>
            </>
          )}
        </p>
        <div className="flex items-center gap-2">
          {onSinFechaChange && (
            <Button
              size="sm"
              variant={sinFecha ? 'default' : 'outline'}
              className={
                sinFecha
                  ? `bg-${colorNombre}-700 hover:bg-${colorNombre}-800 text-white`
                  : 'border-gray-300 dark:border-gray-700'
              }
              onClick={() => onSinFechaChange(!sinFecha)}
            >
              <History className="h-4 w-4" />
              Todo el histórico
            </Button>
          )}
          {/* Cuando "Todo el histórico" está activo, el selector no aplica: se
              atenúa y se desactiva (toggle + control de fecha). */}
          <div className={`flex items-center gap-2 ${sinFecha ? 'opacity-50 pointer-events-none' : ''}`}>
            {soportaRango && (
              <div className="flex items-center rounded-md border border-gray-300 dark:border-gray-700 p-0.5">
                {(['mes', 'rango'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => onModoChange?.(m)}
                    className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                      modo === m
                        ? `bg-${colorNombre}-700 text-white`
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {m === 'mes' ? 'Mes' : 'Rango'}
                  </button>
                ))}
              </div>
            )}
            {enRango ? (
              <div className="flex items-center gap-1.5">
                <Input
                  type="date"
                  value={rangoPersonalizado.desde || ''}
                  min={minDate ?? undefined}
                  max={rangoPersonalizado.hasta || maxDate || undefined}
                  onChange={(e) => setRango('desde', e.target.value)}
                  className="h-9 w-[9.5rem]"
                  aria-label="Desde"
                />
                <span className="text-xs text-muted-foreground">→</span>
                <Input
                  type="date"
                  value={rangoPersonalizado.hasta || ''}
                  min={rangoPersonalizado.desde || minDate || undefined}
                  max={maxDate ?? undefined}
                  onChange={(e) => setRango('hasta', e.target.value)}
                  className="h-9 w-[9.5rem]"
                  aria-label="Hasta"
                />
              </div>
            ) : (
              <MonthFilter value={mes} onChange={onMesChange} minDate={minDate} maxDate={maxDate} />
            )}
          </div>
          <ExportarMenu
            configId={configId}
            tipos={exportTipos}
            filtros={exportFiltros}
            colorNombre={colorNombre}
            onRateLimitChange={setRateRemaining}
          />
        </div>
      </div>
      {rateRemaining !== null && rateRemaining < RATE_LIMIT_WARN_THRESHOLD && (
        <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Quedan <strong>{rateRemaining}</strong> exports en tu ventana horaria.
            Aplica filtros más específicos para no agotar el límite.
          </AlertDescription>
        </Alert>
      )}
    </>
  )
}
